
import * as XLSX from 'xlsx';

/**
 * Extracts product data from an Excel worksheet with support for multiple languages
 */
export const extractProductsFromSheet = (worksheet: XLSX.WorkSheet): Array<{
  name: string;
  category: string | null;
  description: string | null;
  unit_of_measure?: string;
}> => {
  // First, let's check if the worksheet is valid
  if (!worksheet) {
    console.error('Invalid worksheet provided');
    throw new Error('Invalid worksheet: The Excel file appears to be empty or corrupted');
  }
  
  // Get the worksheet range to determine if we have data
  const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1:A1');
  if (range.e.r < 1) {
    console.error('Worksheet is empty or only has a header row');
    throw new Error('The Excel file does not contain enough rows of data');
  }
  
  // Log the worksheet content for debugging
  console.log('Worksheet info:', {
    range: worksheet['!ref'],
    rowCount: range.e.r + 1,
    cellCount: Object.keys(worksheet).filter(key => key !== '!ref' && key !== '!margins').length
  });
  
  // Get first row to examine headers
  const headers: Record<string, string> = {};
  let headerFound = false;
  
  // Examine potential header row
  for (let C = range.s.c; C <= range.e.c; ++C) {
    const cell = worksheet[XLSX.utils.encode_cell({r: 0, c: C})];
    if (cell && cell.t === 's') {
      const header = cell.v.toString().toLowerCase();
      headers[XLSX.utils.encode_col(C)] = header;
      // If we find a product_name or similar header, assume this is indeed a header row
      if (header === 'product_name' || header === 'productname' || header === 'name' || header === 'product') {
        headerFound = true;
      }
    }
  }
  
  console.log('Detected headers:', headers);
  console.log('Header row detected:', headerFound);
  
  // Try different approaches to parse the data
  let data;
  
  try {
    // First attempt with standard column names and UTF-8 encoding
    data = XLSX.utils.sheet_to_json<{
      product_name?: string;
      name?: string;
      measuring_unit?: string;
      unit_of_measure?: string;
      category_name?: string;
      category?: string;
      description?: string;
    }>(worksheet, { 
      raw: false, 
      defval: null, 
      header: headerFound ? undefined : 'A'
    });
    
    console.log('Parsed sheet data (attempt 1):', data.slice(0, 3));
    
    // If we got empty data and headers were not detected, try again with auto headers
    if (data.length === 0 && !headerFound) {
      data = XLSX.utils.sheet_to_json(worksheet, { 
        raw: false, 
        defval: null, 
        header: 'A' 
      });
      console.log('Parsed sheet data (attempt 2, auto headers):', data.slice(0, 3));
    }
  } catch (error) {
    console.error('Error parsing Excel data:', error);
    throw new Error(`Failed to parse Excel data: ${(error as Error).message}`);
  }
  
  if (!data || data.length === 0) {
    console.error('No data parsed from the Excel file');
    throw new Error('No data could be parsed from the Excel file. Please check the format.');
  }
  
  // Skip the header row if we detected it
  const startRow = headerFound ? 0 : 0;
  
  // Process the data rows with proper Unicode handling
  const products = data.slice(startRow).map((row: any, index: number) => {
    // Helper function to find values across multiple potential column names
    const getColumnValue = (possibleNames: string[]): any => {
      // Check direct property access first
      for (const name of possibleNames) {
        if (row[name] !== undefined && row[name] !== null) {
          return row[name];
        }
      }
      
      // Then check case-insensitive match on all keys
      for (const key of Object.keys(row)) {
        if (possibleNames.some(name => key.toLowerCase() === name.toLowerCase())) {
          return row[key];
        }
      }
      
      return null;
    };
    
    // Try to extract values with various column name formats
    const name = getColumnValue([
      'product_name', 'productname', 'name', 'product', 
      'A', 'a', // For auto-generated headers in first column
    ]);
    
    const unit = getColumnValue([
      'measuring_unit', 'measuringunit', 'unit_of_measure', 'unit', 'unitofmeasure',
      'B', 'b', // For auto-generated headers in second column
    ]);
    
    const category = getColumnValue([
      'category_name', 'categoryname', 'category',
      'C', 'c', // For auto-generated headers in third column
    ]);
    
    const description = getColumnValue([
      'description', 'desc', 'product_description', 
      'D', 'd', // For auto-generated headers in fourth column
    ]);
    
    // Debug information for this row
    console.log(`Row ${index + 1} extraction:`, {
      rowData: row,
      extracted: { 
        name: name ? String(name) : null,
        unit_of_measure: unit ? String(unit) : 'kg', 
        category: category ? String(category) : null, 
        description: description ? String(description) : null 
      }
    });
    
    // Validate the product name
    if (!name || typeof name !== 'string' || name.trim() === '') {
      console.warn(`Row ${index + 1}: Invalid or missing product name:`, name);
      return null;
    }
    
    // Clean up category data - ensure it's not an empty string
    let cleanCategory = category ? String(category).trim() : null;
    if (cleanCategory === '') {
      cleanCategory = null;
    }
    
    // Return the product object with all collected data, ensuring proper string handling
    return {
      name: String(name).trim(),
      unit_of_measure: unit ? String(unit).trim() : 'kg',
      category: cleanCategory,
      description: description ? String(description).trim() : null
    };
  }).filter((product): product is NonNullable<typeof product> => product !== null);
  
  console.log(`Successfully extracted ${products.length} products out of ${data.length} rows`);
  
  if (products.length === 0) {
    throw new Error(`No valid products found in the Excel file. Please ensure your file has a 'product_name' column and at least one valid product name.`);
  }
  
  return products;
};
